import { AxiosError } from 'axios';

import { CaseStatuses } from 'modules/cm/domain/Case/CaseStatuses';
import { CaseDetails, CaseStatus } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { Nullable } from 'utils/types';
import { FIELDS } from '../../fields/types';
import { createState } from '../core';
import { ACTION_TYPES, Action, CaseErrorDetails, State } from './types';

import { RepaymentStrategyField } from 'modules/cm/RepaymentStrategy/RepaymentStrategy';
import { CaseError } from 'modules/cm/domain/Case/Error';
import { store } from '../subPages';
import { reducer as subPagesReducer } from './../subPages';
import { default as caseErrorsReducer } from './caseErrors/reducer';
import { default as caseHistoryReducer } from './caseHistory/reducer';
import { default as dueDiligenceReducer } from './dueDiligence/reducer';
import { default as pepperReviewReducer } from './pepperReview/reducer';
import { default as permissionReducer } from './permission/reducer';
import { default as productsReducer } from './products/reducer';
import { default as readonlyFieldsReducer } from './readonlyFields/reducer';
import { default as caseRefreshReducer } from './refresh/reducer';
import { default as underwritingReducer } from './underwriting/reducer';
import { default as loanDetailsReducer } from './update/reducer';

const initialState = createState<CaseDetails, Nullable<AxiosError<CaseErrorDetails>>, Nullable<CaseError>>(
  {
    case: {
      status: CaseStatuses.NotSet,
      applicants: [],
      contact: {
        adviserName: null,
        adviserFee: null,
        adviserPhone: null,
        adviserEmail: null,
        standardCommissionTerms: null,
        enhancedCommissionTerms: null,
        ongoingCommissionTermsUpfrontFee: null,
        ongoingCommissionTermsOngoingFee: null,
        firmName: null,
        firmFcaNumber: null,
        firmAddress: null,
        networkFcaNumber: null,
        adviserFeeLastPaymentMoment: null,
        packagerFee: null,
        packagerFeeLastPaymentMoment: null,
        solicitor: {
          solicitorName: null,
          solicitorEmail: null,
          solicitorCity: null,
          solicitorContactNumber: null,
          solicitorFlatNumber: null,
          solicitorBuildingName: null,
          solicitorBuildingNumber: null,
          solicitorStreet: null,
          solicitorPostcode: null,
        },
        admin: {
          [FIELDS.ADMIN_OFFER_DATE]: null,
          [FIELDS.ADMIN_COT_REC_DATE]: null,
          [FIELDS.ADMIN_FUNDS_REQ_DATE]: null,
          [FIELDS.ADMIN_EXPECTED_COMPLETION_DATE]: null,
          [FIELDS.ADMIN_COMPLETION_DATE]: null,
          [FIELDS.ADMIN_PEPPER_UPLOAD_DATE]: null,
          [FIELDS.ADMIN_PEPPER_ACCOUNT_ID]: null,
          [FIELDS.ADMIN_ORIGINATOR_ID]: null,
          [FIELDS.ADMIN_DECLINE_DATE]: null,
          [FIELDS.ADMIN_SWAP_COST]: null,
          [FIELDS.ADMIN_EXPECTED_MATURITY]: null,
        },
      },
      identifier: '',
      rationale: '',
      offerPackUrl: '',
      preOfferEsisUrl: '',
      loanDetails: {
        applicationType: null,
        accountName: null,
        accountNumber: null,
        actualPropertyValue: null,
        bankOrBuildingSocietyName: null,
        createdBy: null,
        creditPolicyExemptionRationale: null,
        currentMortgageProvider: null,
        delphiScore: null,
        estimatedPropertyValue: null,
        id: null,
        netLoan: null,
        reinspectionFee: null,
        isBuyToLet: null,
        hasAdverseCredit: null,
        isCreditPolicyExemption: null,
        isFirstTimeBuyer: null,
        isPrivateSale: null,
        loanAmountPurchase: null,
        loanAmountRemortgage: null,
        loanAssistingOthers: null,
        loanDebtConsolidation: null,
        loanDiscretionarySpend: null,
        loanHomeImprovements: null,
        monthlyBasicSpend: null,
        owner: null,
        pepperPurposeOfTheLoan: null,
        postcode: null,
        productCode: null,
        productFeePaymentMethod: null,
        propertyPurchasePrice: null,
        purposeOfCapitalRaise: null,
        purposeOfTheLoan: null,
        sortCode: null,
        sourceOfDeposit: null,
        stage: null,
        status: null,
        updatedAt: null,
        valuationFeePayment: null,
        depositSourceBuilders: null,
        depositSourceDetails: null,
        depositSourceEquityFromSale: null,
        depositSourceGifts: null,
        depositSourceOther: null,
        depositSourceSavings: null,
        hasMarketingEmail: false,
        hasMarketingSms: false,
        hasMarketingPhone: false,
        hasMarketingPost: false,
        isPepperUploadApproved: false,
        [FIELDS.FCA_LOAN_PURPOSE]: null,
        [FIELDS.OUTSTANDING_MORTGAGE_AMOUNT]: null,
        [FIELDS.SOURCE_OF_FUNDS_TO_CLEAR_OUTSTANDING_MORTGAGE]: null,
        [FIELDS.EXISTING_MORTGAGE_STATUS]: null,
        additionalDetails: null,
        annualCouncilTax: null,
        annualGroundRent: null,
        loanOtherPurposes: null,
        [FIELDS.DELEGATED_AUTHORITY_LEVEL]: null,
        [FIELDS.INITIAL_ASSESSMENT_APPROVED_BY]: null,
        [FIELDS.INITIAL_ASSESSMENT_APPROVED_DATE]: null,
        [FIELDS.INITIAL_ASSESSMENT_BY]: null,
        [FIELDS.INITIAL_ASSESSMENT_DATE]: null,
        [FIELDS.UW_RECOMMENDATION]: null,
        [FIELDS.UW_SPECIAL_CONDITIONS]: null,
        [FIELDS.AFT_PREFIX]: {
          [FIELDS.AFT_STAGE]: null,
          [FIELDS.AFT_INCOME]: null,
          [FIELDS.AFT_BUFFER]: null,
          [FIELDS.AFT_LIVING_COSTS]: null,
          [FIELDS.AFT_NET_CASH_FLOW]: null,
          [FIELDS.AFT_MAX_LOAN_POSSIBLE]: null,
        },
        [FIELDS.TERM_LENGTH]: null,
        [FIELDS.PRODUCT_TYPE]: null,
        [FIELDS.IS_ON_HOLD_STATUS]: null,
        [FIELDS.IS_CALL_CARRIED_ALL_APPLICANTS]: null,
        [FIELDS.IS_APPLICATION_APPROVED_AUTHORITY_LEVELS]: null,
        [FIELDS.PRODUCT_TIER]: null,
        [FIELDS.PRODUCT_REPAYMENT_TYPE]: null,
      },
      property: {
        [FIELDS.PROPERTY_FLAT_NUMBER]: null,
        [FIELDS.PROPERTY_BUILDING_NAME]: null,
        [FIELDS.PROPERTY_BUILDING_NUMBER]: null,
        [FIELDS.PROPERTY_STREET]: null,
        [FIELDS.PROPERTY_CITY]: null,
        [FIELDS.PROPERTY_POSTCODE]: null,
        [FIELDS.PROPERTY_HAS_OCCUPANTS_OVER17]: null,
        [FIELDS.PROPERTY_TYPE]: null,
        [FIELDS.PROPERTY_IS_PRIMARY_HOME]: null,
        [FIELDS.PROPERTY_FLOOR_NUMBER]: null,
        [FIELDS.PROPERTY_NUMBER_OF_FLOORS_IN_THE_BUILDING]: null,
        [FIELDS.PROPERTY_HAS_LIFT_ACCESS]: null,
        [FIELDS.PROPERTY_TENURE_TYPE]: null,
        [FIELDS.PROPERTY_REMAINING_YEARS]: null,
        [FIELDS.PROPERTY_IS_EX_LOCAL_AUTHORITY]: null,
        [FIELDS.PROPERTY_IS_RETIREMENT_HOME]: null,
        [FIELDS.PROPERTY_IS_RESTRICTED_COVENANT]: null,
        [FIELDS.PROPERTY_IS_ABOVE_COMMERCIAL_PREMISES]: null,
        [FIELDS.PROPERTY_COMMERCIAL_PREMISES_DETAILS]: null,
        [FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT]: null,
        [FIELDS.PROPERTY_COMMERCIAL_USAGE_DETAILS]: null,
        [FIELDS.PROPERTY_NUMBER_OF_KITCHENS]: null,
        [FIELDS.PROPERTY_NUMBER_OF_BEDROOMS]: null,
        [FIELDS.PROPERTY_HAS_AGRICULTURAL_RESTRICTIONS]: null,
        [FIELDS.PROPERTY_PROPERTY_YEAR_BUILT]: null,
        [FIELDS.PROPERTY_WARRANTY_PROVIDER]: null,
        [FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD]: null,
        [FIELDS.PROPERTY_PROPERTY_CONSTRUCTION]: null,
        [FIELDS.PROPERTY_PROPERTY_DETAILS]: null,
        [FIELDS.PROPERTY_INSTRUCTION_DATE]: null,
        [FIELDS.PROPERTY_INSPECTION_DATE]: null,
        [FIELDS.PROPERTY_VALUATION_TYPE]: null,
        [FIELDS.PROPERTY_HAS_SPECIALIST_REPORT]: null,
        [FIELDS.PROPERTY_TITLE_NUMBER]: null,
        [FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE]: null,
        [FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE]: null,
        [FIELDS.PROPERTY_ACTUAL_PROPERTY_VALUE]: null,
        [FIELDS.PROPERTY_OCCUPANTS_PREFIX]: [],
      },
      repaymentStrategy: {
        [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: null,
        [RepaymentStrategyField.HaveSaleOfAnotherProperty]: null,
        [RepaymentStrategyField.HaveInvestments]: null,
        [RepaymentStrategyField.HaveEndownments]: null,
        [RepaymentStrategyField.HaveOther]: null,

        [RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing]: null,
        [RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty]: null,
        [RepaymentStrategyField.RepaymentValueOfHaveInvestments]: null,
        [RepaymentStrategyField.RepaymentValueOfHaveEndownments]: null,
        [RepaymentStrategyField.RepaymentValueOfHaveOther]: null,
        [RepaymentStrategyField.DetailsForOther]: null,

        [RepaymentStrategyField.TotalAmountToBeRepaid]: null,
        [RepaymentStrategyField.RepaymentValueTotalCalculated]: null,

        properties: [],
        investments: [],
        endowments: [],
      },
      updatedAt: '',
      assurancePolicies: [],
    },
    resources: {
      gender: [],
      applicationTypes: [],
      caseStatuses: [],
      creditPolicyExemptiones: [],
      pepperPurposesOfTheLoan: [],
      productFeePaymentMethods: [],
      purposesOfTheLoan: [],
      sourcesOfDeposit: [],
      valuationFees: [],
      underwriters: [],
      creditFeePayMoment: [],
      originatorIds: [],
      assetTypes: [],
      products: [],
      title: [],
      proofOfId: [],
      proofOfAddress: [],
      martialStatus: [],
      nationalities: [],
      expenditurePaymentFrequencies: [],
      expenditureTypes: [],
      useOfFunds: [],
      employmentStatuses: [],
      employmentNatures: [],
      businessTypes: [],
      propertyTypes: [],
      tenureTypes: [],
      propertyConstructions: [],
      valuationTypes: [],
      assurancePolicyTypes: [],
      assuredPersons: [],
      basisCovers: [],
      fcaLoanPurposes: [],
      existingMortgageStatuses: [],
      authorityLevels: [],
      vulnerabilityCategories: [],
      informationProvidedBy: [],
      productTermLengths: [],
      tiers: [],
    },
    isSaving: false,
  },
  null,
  null,
  store.initialState,
);

const exportFuncs = (state: State = initialState, action: Action) => {
  const caseDetails = state.data as NonNullable<CaseDetails>;
  const map = new Map([
    [
      ACTION_TYPES.SAVING_START.toString(),
      () => ({
        ...state,
        isPending: true,
        isLoaded: false,
        data: { ...caseDetails, isSaving: true },
        error: action.payload[1],
      }),
    ],
    [
      ACTION_TYPES.SAVING_FINISHED.toString(),
      () => ({
        ...state,
        isPending: false,
        isLoaded: true,
        data: { ...caseDetails, isSaving: false },
        error: action.payload[1],
      }),
    ],
    [
      ACTION_TYPES.SUCCESS.toString(),
      () => ({
        ...state,
        isPending: false,
        isLoaded: true,
        initialData: action.payload[0] as CaseDetails,
        data: action.payload[0] as CaseDetails,
        error: action.payload[1],
        subPages: store.initialState,
      }),
    ],
    [ACTION_TYPES.ERROR.toString(), () => ({ ...state, isPending: false, isLoaded: true, error: action.payload[1] })],
    [ACTION_TYPES.SET_ERROR_MODAL.toString(), () => ({ ...state, errorModal: action.payload[0] as Error })],
    [ACTION_TYPES.CLEAR_ERROR_MODAL.toString(), () => ({ ...state, errorModal: null })],
    [ACTION_TYPES.PENDING.toString(), () => ({ ...state, isPending: true, error: null })],
    [
      ACTION_TYPES.SUCCESS_PATCH,
      () => ({
        ...state,
        readonlyFields: action.payload[0] as State['readonlyFields'],
      }),
    ],
    [
      ACTION_TYPES.SET_CASE_STATUS,
      () => ({
        ...state,
        data: {
          ...caseDetails,
          case: {
            ...caseDetails.case,
            status: action.payload[0] as CaseStatus,
          },
        },
      }),
    ],
    ...Object.entries(readonlyFieldsReducer(state, action)),
    ...Object.entries(caseHistoryReducer(state, action)),
    ...Object.entries(caseRefreshReducer(state, action)),
    ...Object.entries(underwritingReducer(state, action)),
    ...Object.entries(pepperReviewReducer(state, action)),
    ...Object.entries(permissionReducer(state, action)),
    ...Object.entries(caseErrorsReducer(state, action)),
    ...Object.entries(dueDiligenceReducer(state, action)),
    ...Object.entries(loanDetailsReducer(state, action)),
    ...Object.entries(subPagesReducer(state, action)),
    ...Object.entries(productsReducer(state, action)),
  ]);

  const actionCallback = map.get(action.type);

  return actionCallback ? actionCallback() : state;
};

export default exportFuncs;
